export const TIMEZONES = Intl.supportedValuesOf('timeZone').map(tz => ({
  id: tz,
  label: tz.replace(/_/g, ' '),
}))

export const EMPTY_SETTINGS = {
  enabled: false,
  settings: {
    clock: { timezone: '', format: '24h' },
    weather: {
      location_name: '',
      latitude: null,
      longitude: null,
      units: 'metric',
    },
    news_card: { feed_url: '', source_label: '', show_images: true },
    ticker: { source: 'rss', feed_url: '', messages: [] },
    branding: { font: '', colour: '' },
  },
}

export const TICKER_MAX_MESSAGES = 10
export const TICKER_MESSAGE_MAX_LENGTH = 200

// Ids must match WidgetFrameTheme::FONTS in app/DisplayRenderer/WidgetFrameTheme.php.
export const WIDGET_FONTS = [
  { id: '', label: 'Default (Arial)' },
  { id: 'bebas-neue', label: 'Bebas Neue' },
  { id: 'dm-sans', label: 'DM Sans' },
  { id: 'figtree', label: 'Figtree' },
  { id: 'inter', label: 'Inter' },
  { id: 'lato', label: 'Lato' },
  { id: 'lora', label: 'Lora' },
  { id: 'manrope', label: 'Manrope' },
  { id: 'merriweather', label: 'Merriweather' },
  { id: 'montserrat', label: 'Montserrat' },
  { id: 'noto-sans', label: 'Noto Sans' },
  { id: 'nunito', label: 'Nunito' },
  { id: 'open-sans', label: 'Open Sans' },
  { id: 'oswald', label: 'Oswald' },
  { id: 'outfit', label: 'Outfit' },
  { id: 'playfair-display', label: 'Playfair Display' },
  { id: 'plus-jakarta-sans', label: 'Plus Jakarta Sans' },
  { id: 'poppins', label: 'Poppins' },
  { id: 'quicksand', label: 'Quicksand' },
  { id: 'raleway', label: 'Raleway' },
  { id: 'roboto', label: 'Roboto' },
  { id: 'roboto-condensed', label: 'Roboto Condensed' },
  { id: 'roboto-slab', label: 'Roboto Slab' },
  { id: 'rubik', label: 'Rubik' },
  { id: 'source-sans-3', label: 'Source Sans 3' },
  { id: 'work-sans', label: 'Work Sans' },
]

export function getDefaultTimezone() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  return TIMEZONES.some(entry => entry.id === tz) ? tz : 'UTC'
}

const FEED_CATALOG = [
  {
    label: 'BBC News',
    url: 'https://feeds.bbci.co.uk/news/rss.xml',
    defaultFor: 'news_card',
  },
  {
    label: 'The Guardian',
    url: 'https://www.theguardian.com/world/rss',
  },
  {
    label: 'Sky News',
    url: 'https://feeds.skynews.com/feeds/rss/home.xml',
    defaultFor: 'ticker',
  },
]

const newsCardDefault = FEED_CATALOG.find(f => f.defaultFor === 'news_card')
const tickerDefault = FEED_CATALOG.find(f => f.defaultFor === 'ticker')

export const DEFAULT_SETTINGS = {
  settings: {
    clock: { timezone: getDefaultTimezone() },
    weather: {
      location_name: 'London, England, United Kingdom',
      latitude: 51.50853,
      longitude: -0.12574,
    },
    news_card: {
      feed_url: newsCardDefault.url,
      source_label: newsCardDefault.label,
    },
    ticker: { feed_url: tickerDefault.url },
  },
}

export const SUGGESTED_FEEDS = FEED_CATALOG.map(({ label, url }) => ({ label, url }))

// Treat API-`null` for any leaf as "not provided" so EMPTY_SETTINGS
// defaults survive the merge for never-saved screens. User-set values
// (including false and 0) take precedence. Missing groups collapse to
// the EMPTY group entirely.
export function buildSeed(initial) {
  return deepMerge(EMPTY_SETTINGS, initial)
}

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function deepMerge(base, override) {
  if (!isPlainObject(override)) return cloneDeep(base)
  const out = {}
  for (const key of Object.keys(base)) {
    const baseVal = base[key]
    const ovVal = override[key]
    if (isPlainObject(baseVal)) {
      out[key] = deepMerge(baseVal, ovVal)
    } else if (ovVal === null || ovVal === undefined) {
      out[key] = baseVal
    } else {
      out[key] = ovVal
    }
  }
  return out
}

function cloneDeep(value) {
  if (!isPlainObject(value)) return value
  const out = {}
  for (const key of Object.keys(value)) {
    out[key] = cloneDeep(value[key])
  }
  return out
}

// Returns a new object with `path` set to `value`. Does not mutate input.
// Creates intermediate plain-object groups as needed.
export function setIn(state, path, value) {
  if (path.length === 0) return value
  const [head, ...rest] = path
  const current = isPlainObject(state) ? state : {}
  const child = head in current ? current[head] : {}
  return { ...current, [head]: setIn(child, rest, value) }
}

// 0 and false count as "set". Only null/undefined/empty-string are empty.
export function isEmpty(value) {
  return value === null || value === undefined || value === ''
}
